// Thin typed wrapper over the Telegram WebApp runtime (telegram-web-app.js,
// loaded in index.html). We deliberately use the global object rather than an
// npm SDK so the surface stays small and version-stable.
//
// SECURITY: initData is the ONLY trusted source of identity. It is the raw
// signed query string we forward to the backend (/identity/rider/telegram-sign),
// which re-validates the HMAC. initDataUnsafe is convenient for UI hints (name)
// but must never be trusted for auth or order ownership.

type ThemeParams = {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
};

type TelegramUser = {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  photo_url?: string;
};

type TelegramLocationData = {
  latitude: number;
  longitude: number;
  altitude?: number | null;
  course?: number | null;
  speed?: number | null;
  horizontal_accuracy?: number | null;
  vertical_accuracy?: number | null;
  course_accuracy?: number | null;
  speed_accuracy?: number | null;
};

type LocationManager = {
  isInited: boolean;
  isLocationAvailable: boolean;
  isAccessRequested: boolean;
  isAccessGranted: boolean;
  init: (callback?: () => void) => LocationManager;
  getLocation: (callback: (location: TelegramLocationData | null) => void) => LocationManager;
  openSettings: () => LocationManager;
};

type WebApp = {
  initData: string;
  initDataUnsafe: { user?: TelegramUser; auth_date?: number; hash?: string };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: ThemeParams;
  viewportHeight: number;
  viewportStableHeight: number;
  isExpanded: boolean;
  ready: () => void;
  expand: () => void;
  close: () => void;
  openLink: (url: string) => void;
  BackButton: { show: () => void; hide: () => void; onClick: (cb: () => void) => void; offClick: (cb: () => void) => void };
  MainButton: {
    setText: (t: string) => void;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  HapticFeedback?: { impactOccurred: (s: 'light' | 'medium' | 'heavy') => void; notificationOccurred: (t: 'error' | 'success' | 'warning') => void };
  LocationManager?: LocationManager;
  onEvent: (event: string, cb: () => void) => void;
  offEvent: (event: string, cb: () => void) => void;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: WebApp };
  }
}

export function getWebApp(): WebApp | null {
  return window.Telegram?.WebApp ?? null;
}

// Whether we are actually running inside Telegram (initData present).
export function isInsideTelegram(): boolean {
  const wa = getWebApp();
  return !!wa && typeof wa.initData === 'string' && wa.initData.length > 0;
}

export function getInitData(): string {
  return getWebApp()?.initData ?? '';
}

export function getTelegramUser(): TelegramUser | null {
  return getWebApp()?.initDataUnsafe?.user ?? null;
}

export async function requestTelegramLocation(): Promise<{ lat: number; lon: number } | null> {
  const manager = getWebApp()?.LocationManager;
  if (!manager) return null;

  if (!manager.isInited) {
    await new Promise<void>((resolve) => manager.init(resolve));
  }

  if (!manager.isLocationAvailable) return null;

  const location = await new Promise<TelegramLocationData | null>((resolve) => {
    manager.getLocation(resolve);
  });

  if (!location) return null;

  return {
    lat: location.latitude,
    lon: location.longitude,
  };
}

// Call once on app boot: tell Telegram we're ready and take the full height.
export function initTelegram(): void {
  const wa = getWebApp();
  if (!wa) return;
  wa.ready();
  wa.expand();
}

// Map Telegram theme params to CSS variables so the UI follows the user's theme.
export function applyTelegramTheme(): void {
  const wa = getWebApp();
  const root = document.documentElement;
  const tp = wa?.themeParams ?? {};
  const set = (cssVar: string, value: string | undefined, fallback: string) =>
    root.style.setProperty(cssVar, value ?? fallback);

  set('--tg-bg', tp.bg_color, '#0f1115');
  set('--tg-text', tp.text_color, '#ffffff');
  set('--tg-hint', tp.hint_color, '#8a8f98');
  set('--tg-link', tp.link_color, '#4aaeff');
  set('--tg-button', tp.button_color, '#2ea6ff');
  set('--tg-button-text', tp.button_text_color, '#ffffff');
  set('--tg-secondary-bg', tp.secondary_bg_color, '#1b1f27');
  root.dataset.theme = wa?.colorScheme ?? 'dark';
}

export function haptic(type: 'success' | 'error' | 'warning'): void {
  getWebApp()?.HapticFeedback?.notificationOccurred(type);
}
